import React, {ReactElement, useState} from "react";
import {connect} from "react-redux";
import {
    EditorType, ElementsValues, ElementsValuesType, getElementsValues, Nullable,
} from "@data-force/app";
import "./RejectionReason.scss";
import classNames from "classnames";
import {Button} from "antd";
import {CloseCircleOutlined} from "@ant-design/icons";
import {Translate} from "react-localize-redux";
import Paragraph from "antd/lib/typography/Paragraph";
import {Status, TaskEntity} from "../../interfaces/store/actions/payload/task";
import StoreState from "../../interfaces/store/storeState";
import HistoryParams from "../../interfaces/historyParams";
import {
    getHistoryParams, getRejectionReason, isTaskChangeable, isTaskRejected,
} from "../../utils/common";
import {LocalState} from "../../interfaces/GenericLocalState";

interface EllipsisConfig {
    rows?: number
    expandable?: boolean
    suffix?: string
    symbol?: React.ReactNode
    onExpand?: React.MouseEventHandler<HTMLElement>
    onEllipsis?: (ellipsis: boolean) => void
}

interface StateProps {
    rejectionReason: Nullable<string>
    isRejectedTask: boolean
}

type Props = StateProps;

const RejectionReason: React.FC<StateProps> = (props: Props): ReactElement => {
    const {rejectionReason, isRejectedTask}: Props = props;

    const [isExpanded, setIsExpanded]: LocalState<boolean> = useState<boolean>(false);
    const [counter, setCounter]: LocalState<number> = useState<number>(0);

    const toggleExpanded = (): void => {
        setIsExpanded(!isExpanded);
        setCounter(isExpanded ? counter + 1 : counter);
    };
    const translatePath: string = "components.RejectionReason.RejectionReason";
    const ellipsisConfigured: EllipsisConfig = {
        expandable: true,
        rows: 2,
        suffix: "  ",
        symbol: !isExpanded ? <Translate id={`${translatePath}.showMore.text`}/> : "",
        onExpand: toggleExpanded,
    };

    const rejectionReasonClasses: string = classNames({
        "df-rejection-reason": true,
        "df-rejection-reason-expanded": isExpanded,
    });

    return (
        <>
            {isRejectedTask && rejectionReason && (
                <div className={rejectionReasonClasses}>
                    <div className="title">
                        <CloseCircleOutlined/>
                        <Translate id={`${translatePath}.title.text`}/>
                    </div>

                    <Paragraph
                        ellipsis={{...ellipsisConfigured}}
                        key={`df-rejection-${counter}`}
                    >
                        {rejectionReason}
                        {isExpanded && (
                            <Button type="link" onClick={toggleExpanded}>
                                <Translate id={`${translatePath}.showLess.text`}/>
                            </Button>
                        )}
                    </Paragraph>
                </div>
            )}
        </>
    );
};

function mapStateToProps({
    job: {tasks, activeTask},
    project,
    schema: {parsedSchemas, elementsValuesMap},
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: Nullable<EditorType> = parsedSchemas[schemaKey] as EditorType;
    const activeTaskEntity: Nullable<TaskEntity> = tasks.find(
        (task: TaskEntity): boolean => task.payload === activeTask?.id,
    );

    const elementsValues: ElementsValues = activeTaskEntity?.id
        ? getElementsValues({
            metadataId: activeTaskEntity.id,
            elementsValuesType: ElementsValuesType.TASK,
            schemaKey,
            elementsValuesMap,
        })
        : {};
    const rejectionReason: Nullable<string> = getRejectionReason(editor, elementsValues);
    const isRejectedTask: boolean = !!activeTaskEntity
        && (isTaskRejected(activeTaskEntity.status)
            || isTaskChangeable(activeTaskEntity.status)
            || (activeTaskEntity.status === Status.COMPLETED && !!rejectionReason));

    return {
        rejectionReason,
        isRejectedTask,
    };
}

export default connect(mapStateToProps)(RejectionReason);
